<template>
  <DsSnackbar
    v-model="isSnackbarVisible"
    :has-close-button="exportStatus === 'FAILED' || exportStatus === 'IN_PROGRESS'"
    @click-action="isManuallyClosed = true"
  >
    <template #icon>
      <DsSvgIcon
        v-if="exportStatus === 'SUCCESS'"
        theme="dark"
        icon="check"
        color="success"
        size="xs"
      />
      <DsLoadingCircle v-else-if="exportStatus === 'IN_PROGRESS'" :size="20" variant="dark" />
      <DsSvgIcon v-else theme="dark" icon="file-x-03" color="attention" size="xs" />
    </template>
    <template #title>
      <span v-if="exportStatus === 'SUCCESS'">
        {{ $t('scheduleExport.export.doneTitle') }}
      </span>
      <span v-else-if="exportStatus === 'IN_PROGRESS'" class="tw-ds-text-sm">
        {{ $t('scheduleExport.export.inProgressTitle') }}
      </span>
      <span v-else class="tw-ds-text-sm--semibold">
        {{ $t('scheduleExport.export.failedTitle') }}
      </span>
    </template>
    <template #subtitle>
      <template v-if="exportStatus === 'IN_PROGRESS'">
        <span class="tw-ds-text-sm">{{ $t('scheduleExport.export.inProgressText') }}</span>
      </template>
      <template v-if="exportStatus === 'FAILED'">
        <div class="failed-snackbar-content">
          <p>{{ $t('scheduleExport.export.failedFirstLineText') }}</p>
          <p>{{ $t('scheduleExport.export.failedSecondLineText') }}</p>
          <DsButton
            hierarchy="link"
            class="failed-snackbar-button"
            @click="onExportFailedButtonClick"
            ><DsSvgIcon icon="arrow-narrow-left" />
            {{ $t('scheduleExport.export.failedButtonText') }}
          </DsButton>
        </div>
      </template>
    </template>
  </DsSnackbar>
</template>

<script setup lang="ts">
import { ExportRetriedEvent } from '@/features/filter/analytics';
import { useScheduleExportStore } from '@/features/scheduleExport/scheduleExportStore';
import { useLoggingService } from '@/services/logging/composable';

const scheduleExportStore = useScheduleExportStore();
const loggingService = useLoggingService();

const onExportFailedButtonClick = () => {
  loggingService.trackEvent(
    new ExportRetriedEvent({
      exportType: scheduleExportStore.selectedExportType,
    }),
  );
  scheduleExportStore.openExportDialog();
};

const isManuallyClosed = ref(false);

const exportStatus = computed(() => scheduleExportStore.exportStatus);

const isSnackbarVisible = computed({
  get: () => {
    const activeStatuses = ['IN_PROGRESS', 'SUCCESS', 'FAILED'];
    return activeStatuses.includes(scheduleExportStore.exportStatus) && !isManuallyClosed.value;
  },
  set: (isOpen: boolean) => {
    isManuallyClosed.value = !isOpen;
  },
});

watch(
  () => scheduleExportStore.exportStatus,
  () => {
    isManuallyClosed.value = false;
  },
);
</script>

<style scoped>
.failed-snackbar-content {
  @apply tw-ds-text-sm tw-w-[400px] tw-gap-2;
}

.failed-snackbar-button {
  @apply tw-h-10 !tw-text-blue-200;
}
</style>
